import { useEffect, useState } from 'react'
import JSZip from 'jszip'
import Layout from '../components/Layout'
import { auth } from '../firebase'
import { createChildProfile, getInstallerConfigForChild } from '../lib/db'
import { subscribeParentChildren } from '../lib/db'
import { toast } from '../lib/toast'
import type { NavDest } from '../lib/nav'
import type { Child } from '../lib/types'

type BaseAgent = {
  id: string
  icon: string
  title: string
  description: string
  features: string[]
  requirements: string
  outputName: string
  // Windows: ZIP base que se combina con el config.json del hijo
  baseZipUrl?: string
  baseFiles?: { path: string; url: string }[]
  // Android: APK directo (la app ya trae google-services.json, no necesita config.json)
  directApkUrl?: string
}

// Los instaladores se sirven desde el propio sitio (public/downloads), NO desde
// GitHub, para que la descarga funcione siempre en producción.
const BASE_AGENTS: Record<string, BaseAgent> = {
  windows: {
    id: 'windows',
    icon: '💻',
    title: 'Agente Windows',
    description: 'PC de tu hija/o: apps, tiempo de pantalla, filtros web y bloqueo remoto.',
    features: [
      'Monitoreo de apps y tiempo de uso',
      'Filtros web por categorías y edad',
      'Bloqueo de apps y límites de tiempo',
      'Bloqueo remoto de la sesión',
      'Reportes de actividad en tiempo real',
    ],
    requirements: 'Windows 10/11 (64 bits)',
    // Firebase Hosting (plan Spark) prohíbe servir ejecutables. El ZIP base
    // (agente + filtro + blocklists) se guarda como texto base64 y el navegador
    // lo reconstruye al descargar. Se le añade el config.json del hijo.
    baseZipUrl: '/downloads/KidSafeAgent-Base.zip.b64.txt',
    outputName: 'KidSafe-Agent-Windows-Instalador.zip',
  },
  android: {
    id: 'android',
    icon: '📱',
    title: 'Agente Android',
    description: 'Móvil/tablet de tu hija/o: apps, ubicación GPS, llamadas, SMS y contacto.',
    features: [
      'Apps instaladas y tiempo de uso',
      'Bloqueo de apps y límites de tiempo',
      'Ubicación GPS en tiempo real',
      'Registro de llamadas y SMS',
      'Filtros web y geo-cercas',
    ],
    requirements: 'Android 8.0 o superior',
    directApkUrl: '/downloads/KidSafe-Child-Agent.apk.b64.txt',
    outputName: 'KidSafe-Child-Agent.apk',
  },
}

// Firebase Hosting (plan Spark) prohíbe servir binarios ejecutables (.apk/.exe).
// Los guardamos como texto base64 y aquí los reconstruimos como Blob en el navegador.
async function fetchBase64AsBlob(url: string): Promise<Blob> {
  const resp = await fetch(url, { cache: 'no-store' })
  if (!resp.ok) throw new Error(`No se pudo descargar el instalador (${resp.status})`)
  const b64 = (await resp.text()).replace(/\s+/g, '')
  const bin = atob(b64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return new Blob([bytes])
}

// Detecta móvil/tablet (Android/iOS) para ocultar el agente de Windows en ellos.
function useIsMobile(): boolean {
  const [isMobile, setIsMobile] = useState(false)
  useEffect(() => {
    const check = () => {
      const ua = navigator.userAgent || ''
      const mobileUA = /Android|iPhone|iPad|iPod|Opera Mini|IEMobile|Mobile/i.test(ua)
      const isWindows = /Windows/i.test(ua)
      const coarse = window.matchMedia?.('(pointer: coarse)').matches ?? false
      const narrow = window.matchMedia?.('(max-width: 1024px)').matches ?? false
      // Nunca ocultamos Windows en un PC (aunque tenga pantalla táctil).
      setIsMobile(!isWindows && (mobileUA || (coarse && narrow)))
    }
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])
  return isMobile
}

export default function DownloadsPage({
  onBack,
  isAdmin,
  onOpenAdmin,
  onOpenAccount,
}: {
  onBack: () => void
  isAdmin?: boolean
  onOpenAdmin?: () => void
  onOpenAccount?: () => void
}) {
  const [children, setChildren] = useState<{ uid: string; data: Child }[]>([])
  const [parentUid, setParentUid] = useState<string>('')
  const [parentEmail, setParentEmail] = useState<string>('')
  const [newChildName, setNewChildName] = useState('')
  const [creating, setCreating] = useState(false)
  const [downloading, setDownloading] = useState<string | null>(null)
  // selector independiente por plataforma
  const [selectedByPlatform, setSelectedByPlatform] = useState<Record<string, string>>({ windows: '', android: '' })
  const [creds, setCreds] = useState<{ name: string; email: string; password: string } | null>(null)
  const isMobile = useIsMobile()

  useEffect(() => {
    if (!auth.currentUser) return
    const uid = auth.currentUser.uid
    const email = auth.currentUser.email ?? ''
    setParentUid(uid)
    setParentEmail(email)
    const unsub = subscribeParentChildren(uid, (list) => {
      setChildren(list)
      setSelectedByPlatform((prev) => {
        const next = { ...prev }
        for (const k of Object.keys(BASE_AGENTS)) {
          if (!next[k] && list.length > 0) next[k] = list[0].uid
        }
        return next
      })
    })
    return () => unsub()
  }, [])

  const handleCreateChild = async () => {
    if (!newChildName.trim()) { toast('Escribe el nombre del niño/a', 'error'); return }
    if (!parentUid || !parentEmail) { toast('No hay sesión de padre', 'error'); return }
    setCreating(true)
    try {
      const creds = await createChildProfile(parentUid, parentEmail, newChildName.trim())
      toast(`✅ Perfil "${newChildName}" creado. Usa estas credenciales en la app del dispositivo.`, 'success')
      setCreds({ name: newChildName.trim(), email: creds.email, password: creds.password })
      setNewChildName('')
      // auto-seleccionar el nuevo hijo en ambos selectores si estaban vacíos
      setSelectedByPlatform((prev) => {
        const next = { ...prev }
        for (const k of Object.keys(BASE_AGENTS)) if (!next[k]) next[k] = creds.childUid
        // si había hijos, seleccionar el nuevo en ambos para feedback inmediato
        if (children.length > 0) { next.windows = creds.childUid; next.android = creds.childUid }
        return next
      })
    } catch (e: unknown) {
      toast((e as { message?: string }).message ?? 'Error al crear el perfil', 'error')
    } finally { setCreating(false) }
  }

  const showCredentials = async (childUid: string) => {
    try {
      const c = await getInstallerConfigForChild(parentUid, childUid)
      const name = children.find((x) => x.uid === childUid)?.data.name ?? 'Hijo/a'
      setCreds({ name, email: c.email, password: c.password })
    } catch (e: unknown) {
      toast((e as { message?: string }).message ?? 'No se pudieron obtener las credenciales', 'error')
    }
  }

  const copyCreds = () => {
    if (!creds) return
    const text = `KidSafe\nUsuario: ${creds.email}\nContraseña: ${creds.password}`
    try {
      navigator.clipboard.writeText(text)
      toast('📋 Credenciales copiadas', 'success')
    } catch {
      toast('No se pudo copiar; anótalas manualmente', 'error')
    }
  }

  const handleDownloadForPlatform = async (platform: 'windows' | 'android') => {
    const childUid = selectedByPlatform[platform]
    if (!childUid) { toast('Selecciona un perfil de hijo primero', 'error'); return }
    const child = children.find(c => c.uid === childUid)
    if (!child) { toast('Perfil no encontrado', 'error'); return }
    setDownloading(platform + ':' + childUid)
    try {
      const creds = await getInstallerConfigForChild(parentUid, childUid)
      await downloadCustomInstaller(platform, creds)
      toast(`📥 Instalador ${platform === 'windows' ? 'Windows' : 'Android'} para "${child.data.name}" descargado`, 'success')
    } catch (e: unknown) {
      toast((e as { message?: string }).message ?? 'Error al generar instalador', 'error')
    } finally { setDownloading(null) }
  }

  const downloadCustomInstaller = async (platform: 'windows' | 'android', creds: { configJson: string; childUid: string }) => {
    const agent = BASE_AGENTS[platform]
    const zip = new JSZip()
    // Cache-busting: evita que el navegador/proxy sirva un ZIP viejo de caché tras
    // publicar una versión nueva (raw.githubusercontent usa max-age=300 pero el disk
    // cache puede ignorarlo). El parámetro ?v= no afecta el contenido servido.
    const bustCache = (url: string) => `${url}${url.includes('?') ? '&' : '?'}_cb=${Date.now()}`
    if (agent.baseZipUrl) {
      const baseZipBlob = await fetchBase64AsBlob(bustCache(agent.baseZipUrl))
      const baseZip = await JSZip.loadAsync(baseZipBlob)
      for (const [path, file] of Object.entries(baseZip.files)) {
        if (!file.dir) {
          const content = await file.async('arraybuffer')
          zip.file(path, content)
        }
      }
      zip.file('config.json', creds.configJson)
    } else if (agent.baseFiles) {
      const filePromises = agent.baseFiles.map(async (f) => {
        try {
          const resp = await fetch(bustCache(f.url), { cache: 'no-store' })
          if (!resp.ok) throw new Error(`Failed to fetch ${f.url}`)
          const blob = await resp.blob()
          zip.file(f.path, await blob.arrayBuffer())
        } catch (e) { console.error(e) }
      })
      await Promise.all(filePromises)
      // El APK se vincula con este config.json (credenciales del hijo)
      zip.file('config.json', creds.configJson)
    }
    const content = await zip.generateAsync({ type: 'blob' })
    const url = URL.createObjectURL(content)
    const childName = children.find(c => c.uid === creds.childUid)?.data.name ?? 'hijo'
    const safeName = childName.replace(/[^a-zA-Z0-9]/g, '_')
    const outName = platform === 'windows' ? `KidSafe-Windows-${safeName}.zip` : `KidSafe-Android-${safeName}.zip`
    const a = document.createElement('a')
    a.href = url; a.download = outName; document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url)
  }

  const handleDownloadApk = async () => {
    const apkUrl = BASE_AGENTS.android.directApkUrl
    if (!apkUrl) return
    // Regla de negocio: no se puede descargar el APK sin un perfil de hijo creado.
    if (children.length === 0) {
      toast('Primero crea un perfil de hijo/a antes de descargar el APK.', 'error')
      return
    }
    if (!selectedByPlatform.android) {
      toast('Selecciona el perfil del hijo/a para este dispositivo.', 'error')
      return
    }
    setDownloading('android:apk')
    try {
      const blob = await fetchBase64AsBlob(`${apkUrl}${apkUrl.includes('?') ? '&' : '?'}_cb=${Date.now()}`)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = BASE_AGENTS.android.outputName
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      toast('📥 APK descargado. Ábrelo en el móvil/tablet para instalar KidSafe.', 'success')
    } catch (e: unknown) {
      toast((e as { message?: string }).message ?? 'No se pudo descargar el APK', 'error')
    } finally {
      setDownloading(null)
    }
  }

  const handleDownloadUninstaller = async () => {
    try {
      const blob = await fetchBase64AsBlob(`/downloads/KidSafeUninstaller.bat.b64.txt?_cb=${Date.now()}`)
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'KidSafeUninstaller.bat'
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
      toast('📥 Desinstalador descargado', 'success')
    } catch (e: unknown) {
      toast((e as { message?: string }).message ?? 'No se pudo descargar el desinstalador', 'error')
    }
  }

  return (
    <Layout
      title="Descargas"
      onBack={onBack}
      onNavigate={(dest: NavDest) => {
        if (dest === 'home') onBack()
        else if (dest === 'account') onOpenAccount?.()
        else if (dest === 'activity' || dest === 'apps') onBack()
      }}
      isAdmin={isAdmin}
      onAdmin={onOpenAdmin}
      onAccount={onOpenAccount}
    >
      <div className="mx-auto max-w-5xl">
        <div className="mb-8 rounded-2xl border border-brand-200 bg-brand-50 p-5">
          <div className="flex items-start gap-3">
            <span className="text-2xl">📥</span>
            <div>
              <h2 className="font-semibold text-brand-800">Instala KidSafe en los dispositivos de tus hijos</h2>
              <p className="mt-1 text-sm text-brand-700">
                Primero crea el <b>perfil del hijo/a</b> y luego descarga el instalador del dispositivo que corresponda.
                <span className="mt-1 block">
                  <b>Android (móvil/tablet):</b> descarga el APK e instálalo en el dispositivo del hijo.
                  {!isMobile && <> · <b>Windows (PC):</b> descarga el ZIP personalizado y ejecútalo como administrador.</>}
                </span>
              </p>
            </div>
          </div>
        </div>

        {/* Credenciales del hijo (necesarias para iniciar sesión en la app) */}
        {creds && (
          <div className="mb-8 rounded-2xl border-2 border-brand-300 bg-white p-6 shadow-sm">
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="text-lg font-bold text-slate-800">🔑 Credenciales de «{creds.name}»</h3>
                <p className="mt-1 text-sm text-slate-600">
                  Estas son las credenciales del perfil <b>vinculado a tu panel</b>. Inicia sesión con ellas en la app
                  KidSafe del dispositivo (Android/Windows). Si en el dispositivo creaste otra cuenta, cierra sesión y
                  entra con estas; de lo contrario el panel <b>no controlará</b> ese dispositivo.
                </p>
              </div>
              <button type="button" onClick={() => setCreds(null)} className="rounded-lg px-2 py-1 text-slate-400 hover:bg-slate-100">✕</button>
            </div>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Usuario / correo</p>
                <p className="mt-1 break-all font-mono text-sm font-semibold text-slate-800">{creds.email}</p>
              </div>
              <div className="rounded-xl bg-slate-50 p-4 ring-1 ring-slate-200">
                <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Contraseña</p>
                <p className="mt-1 break-all font-mono text-sm font-semibold text-slate-800">{creds.password}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={copyCreds}
              className="mt-4 rounded-xl bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-700"
            >
              📋 Copiar credenciales
            </button>
          </div>
        )}

        {/* Sección independiente: Mis hijos */}
        <div className="mb-8 rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-bold text-slate-800">👨‍👧‍👦 Mis hijos</h3>
            <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">{children.length} perfil(es)</span>
          </div>
          <p className="mt-1 text-sm text-slate-500">Cada hijo es un perfil independiente. Ejemplo: "Lucas - 5 años (Android)" y "María - 7 años (Windows)" son dos perfiles distintos.</p>

          {children.length > 0 ? (
            <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {children.map(({ uid, data }) => (
                <div key={uid} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-brand-100 text-lg">👤</div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-slate-800">{data.name}</p>
                    <p className="truncate text-xs text-slate-500">{data.email}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => showCredentials(uid)}
                    className="shrink-0 rounded-lg border border-brand-300 bg-white px-3 py-1.5 text-xs font-semibold text-brand-700 hover:bg-brand-50"
                  >
                    🔑 Credenciales
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-4 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">Aún no hay perfiles. Crea el primero abajo.</p>
          )}

          <div className="mt-5 flex gap-2">
            <input
              type="text"
              value={newChildName}
              onChange={(e) => setNewChildName(e.target.value)}
              placeholder="Nombre del hijo/a (ej: Lucas, María)"
              className="flex-1 rounded-xl border border-slate-200 px-4 py-2.5 text-sm outline-none focus:border-brand-500"
              onKeyDown={(e) => e.key === 'Enter' && handleCreateChild()}
            />
            <button
              onClick={handleCreateChild}
              disabled={creating || !newChildName.trim()}
              className="rounded-xl bg-brand-600 px-6 py-2.5 font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
            >
              {creating ? '⏳ Creando…' : '+ Crear perfil'}
            </button>
          </div>
          <p className="mt-2 text-xs text-slate-400">El perfil se vincula a tu cuenta. Luego elige abajo en qué dispositivo instalarlo.</p>
        </div>

        <div className="grid gap-8 lg:grid-cols-2">
          {Object.entries(BASE_AGENTS)
            .filter(([platformKey]) => !(isMobile && platformKey === 'windows'))
            .map(([platformKey, agent]) => (
            <article key={platformKey} className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-slate-200">
              <div className="flex items-center gap-4">
                <div className={`flex h-14 w-14 items-center justify-center rounded-2xl text-3xl ${platformKey === 'windows' ? 'bg-blue-100' : 'bg-emerald-100'}`}>{agent.icon}</div>
                <div>
                  <h2 className="text-xl font-bold text-slate-800">{agent.title}</h2>
                  <p className="text-xs text-slate-400">{agent.requirements}</p>
                </div>
              </div>
              <p className="mt-4 text-sm text-slate-600">{agent.description}</p>
              <ul className="mt-4 space-y-2">
                {agent.features.map((f, i) => (<li key={i} className="flex items-center gap-2 text-sm text-slate-600"><span className="font-bold text-emerald-500">✓</span> {f}</li>))}
              </ul>

              <div className="mt-5 space-y-3">
                {agent.directApkUrl ? (
                  <>
                    <label className="block text-sm font-medium text-slate-700">¿Para qué hijo/a es este dispositivo?</label>
                    <select
                      value={selectedByPlatform.android ?? ''}
                      onChange={(e) => setSelectedByPlatform(prev => ({ ...prev, android: e.target.value }))}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-brand-500"
                      disabled={children.length === 0}
                    >
                      {children.length === 0 ? <option value="">— Crea un perfil arriba —</option> : children.map(c => <option key={c.uid} value={c.uid}>{c.data.name} — {c.data.email}</option>)}
                    </select>
                    <button
                      type="button"
                      onClick={handleDownloadApk}
                      disabled={downloading !== null || children.length === 0 || !selectedByPlatform.android}
                      className="flex w-full items-center justify-center rounded-xl bg-brand-600 py-3 font-semibold text-white transition hover:bg-brand-700 active:bg-brand-800 disabled:opacity-60"
                    >
                      {downloading === 'android:apk' ? '⏳ Preparando APK…' : '📥 Descargar APK (instalación directa)'}
                    </button>
                    {children.length === 0 && (
                      <p className="text-center text-xs font-semibold text-amber-600">
                        Crea primero un perfil de hijo/a para poder descargar el APK.
                      </p>
                    )}
                    <p className="text-center text-[11px] text-slate-400">
                      Al terminar la descarga, abre <b>KidSafe-Child-Agent.apk</b> en el móvil/tablet.
                      Si Android lo pide, activa «Instalar apps desconocidas» para el navegador.
                    </p>
                  </>
                ) : (
                  <>
                    <label className="block text-sm font-medium text-slate-700">¿Para qué hijo es este {platformKey === 'windows' ? 'PC' : 'móvil'}?</label>
                    <select
                      value={selectedByPlatform[platformKey] ?? ''}
                      onChange={(e) => setSelectedByPlatform(prev => ({ ...prev, [platformKey]: e.target.value }))}
                      className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm outline-none focus:border-brand-500"
                      disabled={children.length === 0}
                    >
                      {children.length === 0 ? <option value="">— Crea un perfil arriba —</option> : children.map(c => <option key={c.uid} value={c.uid}>{c.data.name} — {c.data.email}</option>)}
                    </select>
                    <button
                      onClick={() => handleDownloadForPlatform(platformKey as 'windows' | 'android')}
                      disabled={downloading !== null || !selectedByPlatform[platformKey]}
                      className="w-full rounded-xl bg-brand-600 py-3 font-semibold text-white hover:bg-brand-700 disabled:opacity-60"
                    >
                      {downloading === platformKey + ':' + selectedByPlatform[platformKey] ? '⏳ Generando instalador…' : `📥 Descargar instalador ${platformKey === 'windows' ? 'Windows' : 'Android'} para ${children.find(c=>c.uid===selectedByPlatform[platformKey])?.data.name ?? 'hijo seleccionado'}`}
                    </button>
                    <p className="text-center text-[11px] text-slate-400">ZIP con KidSafeAgent.exe + config.json del hijo. Ejecuta como administrador.</p>
                  </>
                )}
              </div>
            </article>
          ))}
        </div>

        {/* Guía de instalación en Android (Play Protect) */}
        <section className="mt-12 rounded-2xl border-2 border-emerald-200 bg-emerald-50 p-6">
          <div className="flex items-center gap-3">
            <span className="text-3xl">📲</span>
            <div>
              <h2 className="text-xl font-bold text-emerald-800">Cómo instalar el APK en Android (si Play Protect lo bloquea)</h2>
              <p className="text-sm text-emerald-700">
                Es normal: Android bloquea apps de control parental instaladas fuera de Play Store porque piden permisos
                especiales (SMS, llamadas, ubicación, administrador). Sigue estos pasos una sola vez.
              </p>
            </div>
          </div>

          <ol className="mt-5 grid gap-3 md:grid-cols-2">
            {[
              { n: '1', t: 'Descarga el APK', d: 'Pulsa «Descargar APK» arriba. Se guarda en la carpeta Descargas del dispositivo.' },
              { n: '2', t: 'Abre el archivo', d: 'Desde la notificación o la app «Archivos» → Descargas → toca KidSafe-Child-Agent.apk.' },
              { n: '3', t: 'Permite la instalación', d: 'Si aparece el aviso, activa «Permitir desde esta fuente» para Chrome o Archivos y vuelve atrás.' },
              { n: '4', t: 'Si Play Protect lo bloquea', d: 'Toca «Más detalles» → «Instalar de todos modos». Si no aparece esa opción, usa el paso 5.' },
            ].map((s) => (
              <li key={s.n} className="flex gap-3 rounded-xl bg-white p-4 ring-1 ring-emerald-200">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-emerald-600 text-sm font-bold text-white">{s.n}</span>
                <div>
                  <p className="font-semibold text-slate-800">{s.t}</p>
                  <p className="mt-0.5 text-sm text-slate-600">{s.d}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-4">
            <p className="font-semibold text-amber-800">Paso 5 · Si no hay opción «Instalar de todos modos»: desactiva Play Protect temporalmente</p>
            <ol className="mt-2 list-decimal space-y-1 pl-5 text-sm text-amber-900">
              <li>Abre la app <b>Google Play Store</b>.</li>
              <li>Toca tu <b>foto de perfil</b> (arriba a la derecha) → <b>Play Protect</b>.</li>
              <li>Toca el <b>engranaje ⚙️</b> (arriba a la derecha).</li>
              <li>Desactiva <b>«Analizar apps con Play Protect»</b>.</li>
              <li>Vuelve e instala el APK (pasos 2–4).</li>
              <li>Cuando termine, <b>vuelve a activar</b> Play Protect.</li>
            </ol>
            <p className="mt-2 text-xs text-amber-700">
              También puedes instalar el APK desde un PC con <b>adb install</b> (no pasa por Play Protect).
            </p>
          </div>

          <p className="mt-4 text-sm text-emerald-800">
            Al abrir KidSafe por primera vez, acepta todos los permisos (ubicación, SMS, llamadas, uso de apps,
            administrador de dispositivos, VPN) y regístrate como <b>hijo</b> con el correo del padre.
          </p>
        </section>

        <section className="mt-12">
          <h2 className="mb-6 text-center text-2xl font-bold text-slate-800">¿Cómo funciona?</h2>
          <div className="grid gap-6 md:grid-cols-4">
            {[
              { n: '1', t: 'Crea perfil del hijo', d: 'Un perfil por niño (Lucas 5 años, María 7 años). Independiente.' },
              { n: '2', t: 'Elige el dispositivo', d: 'En la tarjeta Windows o Android, selecciona para qué hijo es ese PC/móvil.' },
              { n: '3', t: 'Descarga instalador personalizado', d: 'Se genera un ZIP único con las credenciales de ese hijo.' },
              { n: '4', t: 'Instala y listo', d: 'Ejecuta en el dispositivo del niño. Aparece en tu panel.' },
            ].map((s) => (
              <article key={s.n} className="relative rounded-2xl bg-white p-5 pl-14 ring-1 ring-slate-200">
                <span className="absolute left-4 top-5 flex h-8 w-8 items-center justify-center rounded-full bg-brand-600 text-sm font-bold text-white">{s.n}</span>
                <h4 className="font-semibold text-slate-800">{s.t}</h4>
                <p className="mt-1 text-sm text-slate-500">{s.d}</p>
              </article>
            ))}
          </div>
        </section>

        {/* Sección Solo SuperAdmin: Herramientas de limpieza */}
        {isAdmin && (
          <section className="mt-12 rounded-2xl border-2 border-amber-200 bg-amber-50 p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-2xl">🛠️</span>
              <div>
                <h2 className="text-xl font-bold text-amber-800">Herramientas de Administrador (Solo SuperAdmin)</h2>
                <p className="text-sm text-amber-700">Herramientas para limpieza y mantenimiento del sistema.</p>
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <article className="rounded-xl bg-white p-5 ring-1 ring-amber-200">
                <div className="flex items-center gap-3">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-rose-100 text-xl">🗑️</span>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-800">Desinstalador Universal KidSafe</h3>
                    <p className="mt-1 text-sm text-slate-600">
                      Script <b>standalone</b> que elimina <b>completamente</b> el agente KidSafe de cualquier PC:
                      servicio, tareas, archivos, filtros hosts, políticas de registro.
                    </p>
                    <ul className="mt-2 space-y-1 text-xs text-slate-500">
                      <li>✓ No requiere código de desinstalación ni login</li>
                      <li>✓ Se auto-eleva a Administrador (UAC)</li>
                      <li>✓ Limpia: servicio, tareas, archivos, hosts, registro</li>
                      <li>✓ Compatible con antivirus (script legible, no .exe)</li>
                    </ul>
                    <button
                      type="button"
                      onClick={handleDownloadUninstaller}
                      className="mt-4 inline-flex items-center gap-2 rounded-xl bg-rose-600 px-4 py-2.5 font-semibold text-white transition hover:bg-rose-700"
                    >
                      📥 Descargar Desinstalador (.bat)
                    </button>
                    <p className="mt-2 text-[11px] text-slate-400">
                      Después de descargar: clic derecho → "Ejecutar como administrador" (o doble clic; pide permisos solo). Escribe <b>SI</b> cuando lo pida.
                    </p>
                  </div>
                </div>
              </article>
              <article className="rounded-xl bg-white p-5 ring-1 ring-amber-200">
                <div className="flex items-center gap-3">
                  <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-100 text-xl">🔧</span>
                  <div className="flex-1">
                    <h3 className="font-semibold text-slate-800">Próximas herramientas</h3>
                    <p className="mt-1 text-sm text-slate-600">Espacio reservado para futuras utilidades de administrador.</p>
                  </div>
                </div>
              </article>
            </div>
          </section>
        )}
      </div>
    </Layout>
  )
}
